import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  RefreshCw,
} from 'lucide-react';
import FinanceTransactionForm, { transactionToFormValues } from './FinanceTransactionForm';
import FinanceOverview from './FinanceOverview';
import { useToast } from '../features/toast/ToastContext';

function monthStartISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatRM(n) {
  const v = Number(n) || 0;
  return `RM ${v.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function TransactionTable({ rows, onEdit, onDelete, emptyLabel, showPayment = true }) {
  if (!rows.length) {
    return <p className="jet-finance-empty">{emptyLabel}</p>;
  }
  return (
    <div className="jet-table-wrap">
      <table className="jet-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Country</th>
            <th>Category</th>
            {showPayment ? <th>Payment</th> : null}
            <th>Description</th>
            <th>Ref</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>
              <td>{formatDate(row.transactionDate)}</td>
              <td>{row.country || '—'}</td>
              <td>{row.categoryName}</td>
              {showPayment ? <td>{row.paymentMethod || '—'}</td> : null}
              <td>{row.description || '—'}</td>
              <td>{row.reference || '—'}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatRM(row.amount)}</td>
              <td>
                <div className="jet-finance-row-actions">
                  <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={() => onEdit(row)} title="Edit">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button type="button" className="jet-btn jet-btn-danger jet-btn-icon" onClick={() => onDelete(row._id)} title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminFinancePanel({ apiBase, authToken, isActive }) {
  const toast = useToast();
  const [panelTab, setPanelTab] = useState('overview');
  const [dateFrom, setDateFrom] = useState(monthStartISO);
  const [dateTo, setDateTo] = useState(todayISO);
  const [summary, setSummary] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [newSubParentId, setNewSubParentId] = useState('');
  const [newSubType, setNewSubType] = useState('expense');

  const expenseCategories = useMemo(
    () => categories.filter((c) => c.type === 'expense'),
    [categories],
  );
  const incomeCategories = useMemo(
    () => categories.filter((c) => c.type === 'income'),
    [categories],
  );
  const parentOptions = newSubType === 'income' ? incomeCategories : expenseCategories;

  const loadData = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');
    try {
      const q = `from=${encodeURIComponent(dateFrom)}&to=${encodeURIComponent(dateTo)}`;
      const headers = { Authorization: `Bearer ${authToken}` };
      const [sumRes, incRes, expRes, catRes] = await Promise.all([
        fetch(`${apiBase}/api/finance/summary?${q}`, { headers }),
        fetch(`${apiBase}/api/finance/transactions?${q}&type=income`, { headers }),
        fetch(`${apiBase}/api/finance/transactions?${q}&type=expense`, { headers }),
        fetch(`${apiBase}/api/finance/categories`, { headers }),
      ]);
      const sumJson = await sumRes.json().catch(() => ({}));
      const incJson = await incRes.json().catch(() => ({}));
      const expJson = await expRes.json().catch(() => ({}));
      const catJson = await catRes.json().catch(() => ({}));
      if (!sumRes.ok) throw new Error(sumJson.message || 'Failed to load summary');
      setSummary(sumJson.data);
      setIncomes(Array.isArray(incJson.data) ? incJson.data : []);
      setExpenses(Array.isArray(expJson.data) ? expJson.data : []);
      setCategories(Array.isArray(catJson.data) ? catJson.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, dateFrom, dateTo]);

  useEffect(() => {
    if (isActive && authToken) loadData();
  }, [isActive, authToken, loadData]);

  const saveTransaction = async (type, payload, editId = null) => {
    setSaving(true);
    setError('');
    try {
      const isEdit = Boolean(editId);
      const res = await fetch(
        `${apiBase}/api/finance/transactions${isEdit ? `/${editId}` : ''}`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(isEdit ? payload : { type, ...payload }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to save');
      if (type === 'income') {
        setShowIncomeForm(false);
        setEditingIncome(null);
      } else {
        setShowExpenseForm(false);
        setEditingExpense(null);
      }
      await loadData();
      toast.success(isEdit ? 'Transaction updated' : 'Transaction saved');
    } catch (err) {
      setError(err.message || 'Failed to save');
      toast.error(err.message || 'Failed to save');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const startAddIncome = () => {
    setEditingIncome(null);
    setShowIncomeForm(true);
  };

  const startAddExpense = () => {
    setEditingExpense(null);
    setShowExpenseForm(true);
  };

  const startEditIncome = (row) => {
    setShowIncomeForm(false);
    setEditingIncome(row);
  };

  const startEditExpense = (row) => {
    setShowExpenseForm(false);
    setEditingExpense(row);
  };

  const cancelIncomeForm = () => {
    setShowIncomeForm(false);
    setEditingIncome(null);
  };

  const cancelExpenseForm = () => {
    setShowExpenseForm(false);
    setEditingExpense(null);
  };

  const removeTransaction = async (id, label) => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    try {
      const res = await fetch(`${apiBase}/api/finance/transactions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Delete failed');
      await loadData();
      toast.success('Transaction deleted');
    } catch (err) {
      setError(err.message || 'Delete failed');
      toast.error(err.message || 'Delete failed');
    }
  };

  const addSubcategory = async () => {
    if (!newSubParentId || !newSubName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/api/finance/categories`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newSubName.trim(),
          type: newSubType,
          parentId: newSubParentId,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to add subcategory');
      setNewSubName('');
      setNewSubParentId('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to add subcategory');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'income', label: 'Income' },
    { id: 'expenses', label: 'Expenses' },
    { id: 'categories', label: 'Categories' },
  ];

  return (
    <div className="jet-finance">
      <div className="jet-finance-toolbar">
        <div className="jet-finance-dates">
          <label>
            From
            <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
          <button type="button" className="jet-btn jet-btn-secondary" onClick={loadData} disabled={loading}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        <div className="jet-finance-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`jet-finance-tab${panelTab === t.id ? ' jet-finance-tab--active' : ''}`}
              onClick={() => setPanelTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="jet-finance-error">{error}</p> : null}

      {loading && !summary ? (
        <p className="jet-finance-empty">Loading finance data…</p>
      ) : null}

      {panelTab === 'overview' && summary ? (
        <FinanceOverview
          summary={summary}
          incomes={incomes}
          expenses={expenses}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      ) : null}

      {panelTab === 'income' ? (
        <>
          <div className="jet-card">
            <div className="jet-finance-tab-head">
              <div>
                <h3 className="jet-finance-section-title" style={{ margin: 0 }}>Record income</h3>
                <p className="jet-finance-hint">Log payments received — collection income is also auto-calculated below.</p>
              </div>
              {!showIncomeForm && !editingIncome ? (
                <button type="button" className="jet-btn jet-btn-primary" onClick={startAddIncome}>
                  <Plus className="w-4 h-4" />
                  Add income
                </button>
              ) : null}
            </div>

            {showIncomeForm ? (
              <FinanceTransactionForm
                type="income"
                title="Transaction Details"
                categories={categories}
                apiBase={apiBase}
                authToken={authToken}
                saving={saving}
                onCancel={cancelIncomeForm}
                onError={setError}
                onSubmit={(payload) => saveTransaction('income', payload)}
              />
            ) : null}

            {editingIncome ? (
              <FinanceTransactionForm
                key={editingIncome._id}
                type="income"
                categories={categories}
                initialValues={transactionToFormValues(editingIncome, categories, 'income')}
                isEdit
                apiBase={apiBase}
                authToken={authToken}
                saving={saving}
                onCancel={cancelIncomeForm}
                onError={setError}
                onSubmit={(payload) => saveTransaction('income', payload, editingIncome._id)}
              />
            ) : null}

            <TransactionTable
              rows={incomes}
              onEdit={startEditIncome}
              onDelete={(id) => removeTransaction(id, 'income')}
              emptyLabel="No income recorded for this period. Click Add income to log a payment."
            />
          </div>

          {summary ? (
            <div className="jet-card" style={{ marginTop: '1rem' }}>
              <h3 className="jet-finance-section-title">Auto from collections</h3>
              <p className="jet-finance-hint">
                Invoice totals from collections (boxes + charges). Payments on receipts: {formatRM(summary.paymentsReceived?.total)}
              </p>
              {summary.collectionIncome?.items?.length ? (
                <div className="jet-table-wrap">
                  <table className="jet-table">
                    <thead>
                      <tr>
                        <th>Collection No.</th>
                        <th>Shipper</th>
                        <th>Date</th>
                        <th>Clearing</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.collectionIncome.items.map((row) => (
                        <tr key={row.collectionId || row.collectionNo}>
                          <td style={{ fontWeight: 600 }}>{row.collectionNo}</td>
                          <td>{row.shipperName || '—'}</td>
                          <td>{formatDate(row.date)}</td>
                          <td>{row.clearingType || '—'}</td>
                          <td style={{ textAlign: 'right' }}>{formatRM(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="jet-finance-empty">No collection income in this period.</p>
              )}
            </div>
          ) : null}
        </>
      ) : null}

      {panelTab === 'expenses' ? (
        <div className="jet-card">
          <div className="jet-finance-tab-head">
            <div>
              <h3 className="jet-finance-section-title" style={{ margin: 0 }}>Record expense</h3>
              <p className="jet-finance-hint">Log business costs by category and subcategory.</p>
            </div>
            {!showExpenseForm && !editingExpense ? (
              <button type="button" className="jet-btn jet-btn-primary" onClick={startAddExpense}>
                <Plus className="w-4 h-4" />
                Add expense
              </button>
            ) : null}
          </div>

          {showExpenseForm ? (
            <FinanceTransactionForm
              type="expense"
              categories={categories}
              apiBase={apiBase}
              authToken={authToken}
              saving={saving}
              onCancel={cancelExpenseForm}
              onError={setError}
              onSubmit={(payload) => saveTransaction('expense', payload)}
            />
          ) : null}

          {editingExpense ? (
            <FinanceTransactionForm
              key={editingExpense._id}
              type="expense"
              categories={categories}
              initialValues={transactionToFormValues(editingExpense, categories, 'expense')}
              isEdit
              apiBase={apiBase}
              authToken={authToken}
              saving={saving}
              onCancel={cancelExpenseForm}
              onError={setError}
              onSubmit={(payload) => saveTransaction('expense', payload, editingExpense._id)}
            />
          ) : null}

          <TransactionTable
            rows={expenses}
            onEdit={startEditExpense}
            onDelete={(id) => removeTransaction(id, 'expense')}
            emptyLabel="No expenses recorded for this period."
            showPayment={false}
          />
        </div>
      ) : null}

      {panelTab === 'categories' ? (
        <div className="jet-card">
          <h3 className="jet-finance-section-title">Categories</h3>
          <p className="jet-finance-hint">Default categories are pre-loaded. Add custom subcategories under any parent.</p>

          <div className="jet-finance-cat-add">
            <select className="form-input" value={newSubType} onChange={(e) => { setNewSubType(e.target.value); setNewSubParentId(''); }}>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
            <select className="form-input" value={newSubParentId} onChange={(e) => setNewSubParentId(e.target.value)}>
              <option value="">Parent category</option>
              {parentOptions.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <input
              type="text"
              className="form-input"
              placeholder="New subcategory name"
              value={newSubName}
              onChange={(e) => setNewSubName(e.target.value)}
            />
            <button type="button" className="jet-btn jet-btn-secondary" onClick={addSubcategory} disabled={saving || !newSubParentId || !newSubName.trim()}>
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          <h4 className="jet-finance-cat-heading">Income categories</h4>
          <div className="jet-finance-cat-tree">
            {incomeCategories.map((cat) => (
              <div key={cat._id} className="jet-finance-cat-group">
                <div className="jet-finance-cat-parent">{cat.name}</div>
                <ul>
                  {(cat.children || []).map((sub) => (
                    <li key={sub._id}>{sub.name}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <h4 className="jet-finance-cat-heading">Expense categories</h4>
          <div className="jet-finance-cat-tree">
            {expenseCategories.map((cat) => (
              <div key={cat._id} className="jet-finance-cat-group">
                <div className="jet-finance-cat-parent">{cat.name}</div>
                <ul>
                  {(cat.children || []).map((sub) => (
                    <li key={sub._id}>{sub.name}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
